import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton, Badge, Popover, Box, Typography, List, ListItem,
  Divider, CircularProgress, Chip, Button
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { studentAPI } from '../../services/api';

const POLL_INTERVAL = 60000; // 1 minute

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anchor, setAnchor] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await studentAPI.getNotifications();
      setNotifications(data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [load]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleOpen = async (e) => {
    setAnchor(e.currentTarget);
    // Mark all unread as read
    const unread = notifications.filter(n => !n.is_read);
    if (unread.length > 0) {
      setLoading(true);
      await Promise.all(unread.map(n => studentAPI.markNotificationRead(n.id).catch(() => {})));
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setLoading(false);
    }
  };

  const handleClose = () => setAnchor(null);

  const open = Boolean(anchor);

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} sx={{ mr: 1 }}>
        <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
          {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { width: 360, maxHeight: 480, borderRadius: 3, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={700} fontSize={15}>Notifications</Typography>
          {loading && <CircularProgress size={16} sx={{ color: 'white' }} />}
        </Box>

        {/* List */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
              <NotificationsNoneIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              <Typography variant="body2" mt={1}>No notifications</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((n, i) => (
                <React.Fragment key={n.id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      py: 1.5,
                      px: 2,
                      bgcolor: n.is_read ? 'transparent' : '#e3f2fd',
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {!n.is_read && (
                          <CircleIcon sx={{ fontSize: 8, color: '#1976d2', flexShrink: 0 }} />
                        )}
                        <Typography
                          variant="body2"
                          fontWeight={n.is_read ? 500 : 700}
                          sx={{ flexGrow: 1 }}
                        >
                          {n.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {formatDate(n.created_at)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.82rem', lineHeight: 1.4, pl: n.is_read ? 0 : 1.5 }}
                      >
                        {n.message}
                      </Typography>
                    </Box>
                  </ListItem>
                  {i < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return date.toLocaleDateString();
}
